import { apiRequest } from './client';

const BASE = '/api/v1/billing';

// Use the same token key as AuthContext
const TOKEN_KEY = 'policyora_token';
function authOpts(extra: RequestInit = {}): RequestInit {
  const token = localStorage.getItem(TOKEN_KEY);
  return {
    ...extra,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(extra.headers ?? {}),
    },
  };
}

// ── Plans ────────────────────────────────────────────────────────────────────
export interface PlanFeatures {
  user_seats: number | string;
  jurisdictions: number | string;
  ai_summaries_per_month: number | string;
  documents_per_month: number | string;
  ai_agents: number | string;
  policies_per_month: number | string;
  multi_jurisdiction: boolean;
  custom_alerts: number | string;
  api_access: boolean;
  team_collaboration: boolean;
  export_reporting: boolean;
  sso: boolean;
  dedicated_support: boolean;
  advanced_ai: boolean;
  predictive_analytics: boolean;
}

export interface Plan {
  tier: string;
  display_name: string;
  monthly_price_usd: string | null;
  annual_price_usd: string | null;
  annual_monthly_equivalent: string | null;
  annual_savings_pct: number;
  is_popular: boolean;
  contact_sales: boolean;
  description: string;
  features: PlanFeatures;
  /** Admin catalogue fields */
  show_on_pricing?: boolean;
  segment?: 'standard' | 'free' | 'law_firm' | 'government' | 'enterprise';
  sort_order?: number;
  disabled?: boolean;
}

export const fetchPlans = (): Promise<Plan[]> =>
  apiRequest<Plan[]>(`${BASE}/plans`);

/** Which ways of paying the backend will actually accept. PayPal is withdrawn
 *  until the live app's credentials are in place, so neither the checkout nor
 *  the pricing FAQ may hard-code it. */
export interface PaymentMethods { gateways: string[] }
export const fetchPaymentMethods = (): Promise<PaymentMethods> =>
  apiRequest<PaymentMethods>(`${BASE}/payment-methods`);
/** Card is the fallback when the call fails: it is always offered, and a
 *  failed call must never add PayPal back. */
export const offersPayPal = (m: PaymentMethods | null | undefined): boolean =>
  !!m && (m.gateways || []).includes('paypal');

// ── Subscription ─────────────────────────────────────────────────────────────
export interface Subscription {
  id: number;
  user_id: number;
  plan_tier: string;
  billing_cycle: 'monthly' | 'annual' | null;
  status: 'trialing' | 'active' | 'cancelled' | 'expired' | 'past_due';
  trial_started_at: string | null;
  trial_ends_at: string | null;
  trial_days_remaining: number | null;
  current_period_start: string | null;
  current_period_end: string | null;
  amount: string | null;
  currency: string | null;
  cancel_at_period_end: boolean;
  cancelled_at: string | null;
  payment_gateway: string | null;
  auto_renews: boolean;
  created_at: string;
}

export const fetchSubscription = (): Promise<Subscription> =>
  apiRequest<Subscription>(`${BASE}/subscription`, authOpts());

export const startTrial = (plan_tier: string): Promise<Subscription> =>
  apiRequest<Subscription>(`${BASE}/trial/start`, authOpts({ method: 'POST', body: JSON.stringify({ plan_tier }) }));

export interface CreateSubscriptionPayload {
  plan_tier: string;
  billing_cycle: 'monthly' | 'annual';
  payment_gateway: 'paypal' | 'stripe';
  stripe_payment_method_id?: string;
  paypal_subscription_id?: string;
}

export const createSubscription = (payload: CreateSubscriptionPayload): Promise<Subscription> =>
  apiRequest<Subscription>(
    `${BASE}/subscribe`,
    authOpts({ method: 'POST', body: JSON.stringify(payload) }),
  );

export const cancelSubscription = (cancelImmediately = false): Promise<Subscription> =>
  apiRequest<Subscription>(
    `${BASE}/cancel`,
    authOpts({ method: 'POST', body: JSON.stringify({ cancel_immediately: cancelImmediately }) }),
  );

export const upgradeSubscription = (plan_tier: string, billing_cycle: string): Promise<Subscription> =>
  apiRequest<Subscription>(
    `${BASE}/upgrade`,
    authOpts({ method: 'PATCH', body: JSON.stringify({ plan_tier, billing_cycle: billing_cycle.toLowerCase() }) }),
  );

// ── Invoices ─────────────────────────────────────────────────────────────────
export interface Invoice {
  id: number;
  invoice_number: string | null;
  pdf_available: boolean;
  gateway: 'paypal' | 'stripe';
  gateway_transaction_id: string | null;
  amount: string;
  currency: string;
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  description: string | null;
  invoice_url: string | null;
  created_at: string;
}

export const fetchInvoices = (): Promise<Invoice[]> =>
  apiRequest<Invoice[]>(`${BASE}/invoices`, authOpts());

/** Download the PolicyOra invoice PDF for a paid/refunded payment (SRS #84). */
export async function downloadInvoicePdf(id: number, admin = false): Promise<void> {
  const token = localStorage.getItem('policyora_token');
  const apiUrl = process.env.REACT_APP_API_BASE_URL ?? '';
  const url = admin ? `${apiUrl}/api/v1/admin/invoices/${id}/pdf` : `${apiUrl}${BASE}/invoices/${id}/pdf`;
  const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const blob = await res.blob();
  const m = /filename="?([^"]+)"?/.exec(res.headers.get('content-disposition') || '');
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href; a.download = m ? m[1] : `invoice-${id}.pdf`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(href);
}

/** Symbols: shipped defaults, extended by master data (SRS #86) once it has loaded. */
export const CURRENCY_SYMBOL: Record<string, string> = { USD: '$', EUR: '€', GBP: '£', INR: '₹', SGD: 'S$', AUD: 'A$', CAD: 'C$' };
export const registerCurrencies = (list: { code: string; symbol: string }[]) => { list.forEach((c) => { CURRENCY_SYMBOL[c.code.toUpperCase()] = c.symbol; }); };
/** A plan quantity for display. "unlimited" stays; 0 means unlimited for seats (the catalogue's convention,
 *  see the admin plan editor's "Seats (0 = unlimited)") but "not included" for allowances such as agents. */
const ZERO_MEANS_UNLIMITED = new Set(['user_seats']);
export const fmtQuantity = (key: string, v: string | number | boolean | null | undefined): string => {
  if (v === 'unlimited') return 'unlimited';
  if (v === 0 || v === '0') return ZERO_MEANS_UNLIMITED.has(key) ? 'unlimited' : 'no';
  return String(v ?? '');
};
/** A quantity the plan does not include at all, so a caller can mark it the way it marks a
 *  switched-off boolean. ``fmtQuantity`` renders such a value "no", and "no document analyses"
 *  beside a tick reads as if it were included. */
export const isQuantityOff = (key: string, v: string | number | boolean | null | undefined): boolean =>
  (v === 0 || v === '0') && !ZERO_MEANS_UNLIMITED.has(key);

export const fmtMoney = (amount: string | number | null | undefined, currency: string | null | undefined): string => {
  const cur = (currency || 'USD').toUpperCase();
  const n = Number(amount ?? 0);
  return `${CURRENCY_SYMBOL[cur] ?? ''}${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${CURRENCY_SYMBOL[cur] ? '' : ` ${cur}`}`;
};

export const initiateCheckout = (plan_tier: string, billing_cycle: string, gateway: string = 'stripe'): Promise<Invoice> =>
  apiRequest<Invoice>(
    `${BASE}/checkout/initiate`,
    authOpts({ method: 'POST', body: JSON.stringify({ plan_tier, billing_cycle, gateway }) }),
  );

// ── PayPal helpers ────────────────────────────────────────────────────────────
export interface PayPalOrderResponse {
  order_id: string;
  approve_url: string;
}

export const createPayPalOrder = (plan_tier: string, billing_cycle: string): Promise<PayPalOrderResponse> =>
  apiRequest<PayPalOrderResponse>(
    `${BASE}/paypal/create-order`,
    authOpts({ method: 'POST', body: JSON.stringify({ plan_tier, billing_cycle }) }),
  );

export const capturePayPalOrder = (
  order_id: string,
  plan_tier: string,
  billing_cycle: string,
): Promise<Subscription> =>
  apiRequest<Subscription>(
    `${BASE}/paypal/capture-order`,
    authOpts({ method: 'POST', body: JSON.stringify({ order_id, plan_tier, billing_cycle }) }),
  );

// ── PQL ───────────────────────────────────────────────────────────────────────
export const logPQLEvent = (event_type: string, event_metadata?: string): void => {
  apiRequest(`${BASE}/pql/event`, authOpts({
    method: 'POST',
    body: JSON.stringify({ event_type, event_metadata }),
  })).catch(() => {/* silent */});
};

// ── Feature gate ─────────────────────────────────────────────────────────────
export interface FeatureCheck {
  allowed: boolean;
  feature: string;
  reason: string | null;
  upgrade_to: string | null;
}

export const checkFeature = (feature: string): Promise<FeatureCheck> =>
  apiRequest<FeatureCheck>(`${BASE}/feature/${feature}`, authOpts());

// ── Contact Messages ──────────────────────────────────────────────────────────
export interface ContactMessage {
  id: number;
  user_id: string;
  user_email?: string;
  user_name?: string;
  user_organization?: string;
  subject: string;
  message: string;
  status: 'unread' | 'read' | 'replied';
  created_at: string;
  attachment_name?: string | null;
  attachment_url?: string | null;
  admin_reply?: string | null;
  replied_at?: string | null;
}

// Multipart (not JSON) so an optional file can ride along. We hand-build the
// request instead of using authOpts/apiRequest, both of which force a JSON
// Content-Type — for FormData the browser must set the multipart boundary itself.
export const sendContactMessage = async (
  data: { subject: string; message: string; file?: File | null },
): Promise<{ id: number; attachment_stored?: boolean }> => {
  const token = localStorage.getItem(TOKEN_KEY);
  const form = new FormData();
  form.append('subject', data.subject);
  form.append('message', data.message);
  if (data.file) form.append('file', data.file);
  const apiBase = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8000';
  const res = await fetch(`${apiBase}${BASE}/contact`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(body || `Request failed with status ${res.status}`);
  }
  return res.json();
};

export const fetchContactMessages = (token: string): Promise<ContactMessage[]> =>
  apiRequest<ContactMessage[]>(`${BASE}/contact/admin`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });

// The signed-in user's own support history (their messages + any admin reply).
export const fetchMyContactMessages = (): Promise<ContactMessage[]> =>
  apiRequest<ContactMessage[]>(`${BASE}/contact/mine`, authOpts());

export const markContactMessageRead = (id: number, token: string): Promise<void> =>
  apiRequest<void>(`${BASE}/contact/${id}/read`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });

// Admin replies to a support message; the reply is shown back to the user.
export const replyToContactMessage = (
  id: number,
  reply: string,
  token: string,
): Promise<{ ok: boolean; admin_reply: string; replied_at: string | null; status: string }> =>
  apiRequest(`${BASE}/contact/${id}/reply`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ reply }),
  });

// ---------------------------------------------------------------------------
// Entitlements (SRS #139/#140) — the server's answer to "what does this plan include"
// ---------------------------------------------------------------------------

export interface FeatureEntitlement {
  allowed: boolean;
  upgrade_to: string | null;
}

export interface Entitlements {
  tier: string | null;
  status: string | null;
  effective_tier: string | null;
  /** plan segment: standard | free | law_firm | government | enterprise — decides matters vs engagements wording */
  segment?: string | null;
  trial_days_remaining: number | null;
  features: Record<string, FeatureEntitlement>;
  quantities: Record<string, number | string | null>;
  jurisdiction_limit: number | null;
  /** navigation entry key → feature it requires */
  navigation: Record<string, string>;
}

export const fetchEntitlements = (): Promise<Entitlements> =>
  apiRequest<Entitlements>(`${BASE}/entitlements`, authOpts());

/** Metered usage this month against the plan's allowances (services/metering.py). */
export interface UsageMeter {
  meter: string; label: string; unit: string; used: number; limit: number | null; remaining: number | null; pct: number | null;
  warning: boolean; exhausted: boolean; monthly: boolean; resets_at: string | null;
}
/** The pooled AI-credit allowance (services/credits.py::balance).
 *  ``enforced`` is false until an administrator puts a positive figure on the
 *  plan, and a plan with no figure must not be shown a credit meter — every
 *  catalogue row predates the field, so it would read "0" for everyone. */
export interface CreditBalance {
  unlimited: boolean; enforced: boolean; effective_tier: string | null;
  allowance: number | null; allowance_used: number; allowance_remaining: number | null;
  purchased_remaining: number; remaining: number | null; spent_this_period: number;
  pct: number | null; period_start: string; resets_at: string;
  /** What this period's credits went on, dearest first. Absent on an older
   *  server, so every reader must treat it as optional. */
  by_action?: CreditSpend[];
}
export interface CreditSpend { action: string; label: string; times: number; credits: number }
export interface Usage { period_start: string; resets_at: string; effective_tier: string | null; meters: UsageMeter[]; credits?: CreditBalance }
export const fetchUsage = (): Promise<Usage> => apiRequest<Usage>(`${BASE}/usage`, authOpts());


// ── payment audit trail (SRS #83) ──────────────────────────────────────────
export interface PaymentEvent {
  id: number; created_at: string | null; user_id: number | null; email?: string | null; subscription_id: number | null; payment_id: number | null;
  gateway: string; kind: string; kind_label: string; outcome: 'ok' | 'failed' | 'ignored' | 'unmatched' | 'duplicate';
  gateway_event_id: string | null; gateway_ref: string | null; amount: string | null; currency: string | null; summary: string;
  details: Record<string, unknown> | null; actor: string | null;
}
export interface PaymentSummary {
  gross_completed: string; refunded: string; net: string; declined_attempts: number;
  by_gateway: Record<string, Record<string, { count: number; total: string }>>;
  events: Record<string, Record<string, number>>;
  webhooks: Record<string, { last_received_at: string | null; days_since: number | null; unmatched: number; duplicates: number }>;
  generated_at: string;
}
export const fetchMyPaymentEvents = () => apiRequest<{ events: PaymentEvent[]; kinds: Record<string, string> }>(`${BASE}/payments/events`, authOpts());
export const adminPaymentEvents = (qs: string) => apiRequest<{ events: PaymentEvent[]; kinds: Record<string, string>; outcomes: string[] }>(`/api/v1/admin/payments/events?limit=500&${qs}`, authOpts());
export const adminPaymentSummary = (qs: string) => apiRequest<PaymentSummary>(`/api/v1/admin/payments/summary?${qs}`, authOpts());
export const adminRefundNote = (paymentId: number, body: { amount?: string; reason: string }) =>
  apiRequest<{ event: PaymentEvent; payment_status: string }>(`/api/v1/admin/payments/${paymentId}/refund-note`, authOpts({ method: 'POST', body: JSON.stringify(body) }));

/** Put the account on a $0 plan (no Stripe/PayPal; active with no end date). */
export const activateFreePlan = (plan_tier: string): Promise<Subscription> =>
  apiRequest<Subscription>(`${BASE}/free/activate`, authOpts({ method: 'POST', body: JSON.stringify({ plan_tier }) }));
